import {useEffect, useRef, useState} from "react";
import {TrackingFunction} from "@webng/liveblog";
import {useApi} from "@webng/react-app-common";
import {ApiClient} from "@webng/tickaroo-api";
import {debugEvent, notifyErrorRaw} from "../analytics/notifyError";


const AB_PERCENTAGE = 1.0;

function createHeartbeatTimeoutFn(ws: WebSocket) {
  return () => {
    console.error("❌ No heartbeat received from server, closing connection");
    ws.close();
  }
}

export function useChangeStream(liveblogId: string, trackingFunction: TrackingFunction) {
  const wsRef = useRef<WebSocket | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const realRetryCount = useRef(0);
  const [ version, setVersion ] = useState<number | undefined>(undefined)
  const [hasErrored, setHasErrored] = useState(false);

  const apiClient = useApi();

  const abId = useRef(Math.random())

  const enableExperiment = abId.current >= (1 - AB_PERCENTAGE);

  const onVisibilityChange = () => {
    if(document.hidden) {
      setHasErrored(true);
      setRetryCount(0);
      realRetryCount.current = 0;
      if(wsRef.current) {
        wsRef.current.close(1000, "Component unmounted");
        wsRef.current = null;
      }
    } else {
      setHasErrored(false);
    }
  }

  const onNetworkChange = () => {
    if(navigator.onLine) {
      setHasErrored(false);
    } else {
      setHasErrored(true);
      setRetryCount(0);
      realRetryCount.current = 0;
      if(wsRef.current) {
        wsRef.current.close(1000, "Component unmounted");
        wsRef.current = null;
      }
    }
  }

  useEffect(() => {
    if(!enableExperiment) return;
    document.addEventListener("visibilitychange", onVisibilityChange, false);
    window.addEventListener("online", onNetworkChange, false);
    window.addEventListener("offline", onNetworkChange, false);
    if(hasErrored || !enableExperiment) return;

    try {
      const url = new URL(apiClient._apiUrl(ApiClient.API_CHANGEBRIDGE_VERSION, {liveblogId: liveblogId}));
      url.protocol = "wss:";
      const ws = new WebSocket(url);
      const heartbeat = setInterval(() => {
        ws.send(JSON.stringify({ type: "ping", ts: Date.now() }));
      }, 20000)

      const heartbeatTimeoutFn = createHeartbeatTimeoutFn(ws);

      let heartbeatTimeout = setTimeout(heartbeatTimeoutFn, 60000)

      ws.onopen = () => {
        debugEvent("ws.onopen", {})
        trackingFunction({t: "w_con", url: liveblogId})
        realRetryCount.current = 0;
      };

      ws.onmessage = (event) => {
        debugEvent("ws.onmessage", {data: event.data})
        const message = JSON.parse(event.data);
        if(message.type === "pong") {
          trackingFunction({t: "w_msg", s: Date.now() - message.ts });
          clearTimeout(heartbeatTimeout);
          heartbeatTimeout = setTimeout(heartbeatTimeoutFn, 60000)
          return;
        } else {
          setVersion(message.version)
        }
      };

      ws.onclose = (event) => {
        debugEvent("ws.onclose", {code: event.code, reason: event.reason, wasClean: event.wasClean})
        if(event.code !== 1000 && event.reason !== "Component unmounted") {
          console.error(`❌ WebSocket error: ${event.code} ${event.reason}`);
          trackingFunction({t: "w_err", s: event.code, url: liveblogId})
          if(realRetryCount.current >= 5) {
            console.error("❌ Too many retries, giving up");
            clearInterval(heartbeat);
            clearTimeout(heartbeatTimeout);
            setHasErrored(true);
            trackingFunction({t: "w_err", s: 4002, url: liveblogId})
          }
          setTimeout(() => {
            setRetryCount((prev) => prev + 1);
            realRetryCount.current += 1;
          }, 5000);
        }
        trackingFunction({t: "w_dis", s: event.code, url: liveblogId});
      };

      ws.onerror = (err) => {
        console.error("⚠️ WebSocket error:", err);
      };

      wsRef.current = ws;

      // Cleanup on unmount
      return () => {
        ws.close(1000, "Component unmounted");
        clearInterval(heartbeat);
        clearTimeout(heartbeatTimeout);
        document.removeEventListener("visibilitychange", onVisibilityChange, false);
        window.removeEventListener("online", onNetworkChange, false);
        window.removeEventListener("offline", onNetworkChange, false);
      };
    } catch (error) {
      console.error("Error creating WebSocket:", error);
      notifyErrorRaw(error)
      setHasErrored(true);
      trackingFunction({t: "w_err", s: 4001, url: liveblogId})
    }
  }, [retryCount, setVersion, liveblogId, hasErrored, setHasErrored, enableExperiment, trackingFunction, apiClient]);

  if(hasErrored || !enableExperiment) return undefined;

  return version;
}
